import { gql, useQuery } from '@apollo/client';
import {
  ArgumentNode,
  DirectiveNode,
  DocumentNode,
  GraphQLDirective,
  GraphQLFormattedError,
  parse,
  visit,
} from 'graphql';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';

import { QueryPlanResponse, SafeQueryPlan } from './queryPlanHelpers';

export type ImplementingService = {
  name: string;
  sdl: string;
  typeDefs: DocumentNode;
  url: string | undefined;
};

// `FederatedImplementingService`s are the subgraphs that have most recently been pushed
// to studio, regardless of the success or failure of composition
// they are different from `SubgraphConfig`s which are subgraph metadata
// for a given composition
export function useSubgraphs(graphRef: GraphRef | null): {
  subgraphs: ImplementingService[] | null;
  subgraphsLoading: boolean;
} {
  const { data, loading } = useQuery<
    GraphQLTypes.UseSubgraphsQuery,
    GraphQLTypes.UseSubgraphsQueryVariables
  >(
    gql`
      query UseSubgraphsQuery($graphId: ID!, $variantId: String!) {
        service(id: $graphId) {
          id
          variant(name: $variantId) {
            id
            subgraphs {
              name
              url
              activePartialSchema {
                sdl
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId as string,
        variantId: graphRef?.graphVariant as string,
      },
      skip: !graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const subgraphs = React.useMemo(() => {
    return data?.service?.variant?.subgraphs
      ? data.service.variant.subgraphs.map((subgraph) => ({
          name: subgraph.name,
          sdl: subgraph.activePartialSchema.sdl,
          typeDefs: parse(subgraph.activePartialSchema.sdl),
          url: subgraph.url ?? undefined,
        }))
      : null;
  }, [data]);
  return { subgraphs, subgraphsLoading: loading };
}

function isInvalidDocumentError(error: GraphQLFormattedError) {
  return /Unexpected empty options after non-colle|syntax error|cannot query field|Invalid value \$[^ ]* for argument|Unknown (type|fragment) "|Missing mandatory value"|Invalid empty selection set for field|Unexpected selection set on leaf field|Missing mandatory value for argument/i.test(
    error.message,
  );
}

function queryPlanHasNode(queryPlan: unknown): queryPlan is SafeQueryPlan {
  return !!(queryPlan as SafeQueryPlan | undefined)?.node;
}
export function useQueryPlan({
  graphRef,
  document,
  hasSupergraphSchema,
}: {
  document: string;
  graphRef: GraphRef | null;
  hasSupergraphSchema: boolean;
}):
  | (QueryPlanResponse & { state: 'success' })
  | { state: 'failed' }
  | { state: 'loading' } {
  const { data, loading, error } = useQuery<
    GraphQLTypes.UseQueryPlanQuery,
    GraphQLTypes.UseQueryPlanQueryVariables
  >(
    gql`
      query UseQueryPlanQuery(
        $graphId: ID!
        $graphVariant: String!
        $document: GraphQLDocument!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            plan(document: $document) {
              json
              object
              text
            }
          }
        }
      }
    `,
    {
      variables: { ...(graphRef as GraphRef), document },
      skip: !graphRef || !document || !hasSupergraphSchema,
      context: appLinkContext({
        ignoreSentryReporting: [isInvalidDocumentError],
      }),
    },
  );

  return useMemo(() => {
    if (!document || !graphRef || !hasSupergraphSchema) {
      return { state: 'failed' };
    }

    if (loading) return { state: 'loading' };

    const text = data?.service?.variant?.plan?.text;
    const object = data?.service?.variant?.plan?.object;

    if (error || !queryPlanHasNode(object) || !text) {
      return { state: 'failed' };
    }

    return { object, text, state: 'success' };
  }, [
    data?.service?.variant?.plan,
    document,
    error,
    graphRef,
    loading,
    hasSupergraphSchema,
  ]);
}

export enum MetaDataKey {
  Name = 'name',
  Url = 'url',
  Description = 'description',
}

export function getGraphMetaData(typeDefs: DocumentNode | undefined) {
  const isMetaDataKey = (key: string): key is MetaDataKey => {
    return Object.values(MetaDataKey).includes(key as MetaDataKey);
  };

  const metaData: Record<MetaDataKey, string> = {
    name: '',
    url: '',
    description: '',
  };

  if (typeDefs) {
    visit(typeDefs, {
      enter: (node) => {
        if (
          node.kind === 'SchemaDefinition' ||
          node.kind === 'SchemaExtension'
        ) {
          const contactDirective = node.directives?.find(
            (directive) => directive.name.value === 'contact',
          );
          if (contactDirective) {
            contactDirective.arguments?.forEach((argument: ArgumentNode) => {
              if (
                argument.value.kind === 'StringValue' &&
                isMetaDataKey(argument.name.value)
              ) {
                metaData[argument.name.value] = argument.value.value;
              }
            });
          }
        }
      },
    });
  }
  return metaData;
}

export function isSupergraphDirective(
  directive: DirectiveNode | GraphQLDirective,
  isSupergraph: boolean, // If the schema isn't a supergraph, nothing is a supergraph directive so we return false
) {
  const name =
    typeof directive.name === 'string' ? directive.name : directive.name.value;
  return isSupergraph && (name.startsWith('join__') || name === 'link');
}
